import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { BackupTranslationInterface } from '../models/backup-translation.model';
import { CustomFieldInterface } from '../models/custom-field.model';
import { LicenseDataInterface } from '../models/license-data.model';
import { PasswordSettingsInterface } from '../models/password-settings.model';
import { SiteEmailConfigInterface } from '../models/site-email-config.model';
import { SiteSettingInterface } from '../models/site-setting.model';
import { SiteSupportedLanguageInterface } from '../models/site-supported-language.model';
import { UserSessionSettingsInterface } from '../models/usersession-settings.model';
import { SiteSettingsRestConnectorService } from '../services/site-settings-rest-connector.service';
import { ActiveInterface, SSOStatusInterface } from '../models/active.model';
import { ErrorStateInterface } from 'app/core/error-handler/state/models';
import { SSOMetadataInterface } from '../models/sso-metadata.model';
import { MessageBannerInterface, MessageInterface, MessageTextInterface } from '../models/message-banner.model';

import { LoadBackupTranslateCompleted, UpdateBackupTranslate, UpdateBackupTranslateCompleted } from './actions/backup-translation.action';
import { LoadCustomFieldsCompleted, UpdateCustomFields, UpdateCustomFieldsCompleted } from './actions/custom-fields.action';
import { LoadEmailConfigCompleted, UpdateEmailConfig, UpdateEmailConfigCompleted } from './actions/email-config.action';
import { LoadPasswordSettings, LoadPasswordSettingsCompleted, UpdatePasswordSettings, UpdatePasswordSettingsCompleted  } from './actions/password-settings.action';
import { LoadSiteLicenseCompleted, UpdateSiteLicense, UpdateSiteLicenseCompleted } from './actions/site-license.action';
import { SiteSettingActionType } from './actions/site-setings-action-type.enum';
import {
  LoadSiteSettingsCompleted,
  UpdateSiteSetting,
  UpdateSiteSettingCompleted,
  UpdateBannerCheck,
  UpdateBannerCheckCompleted,
  LoadBannerCompleted,
  UpdateBannerMessage,
  UpdateBannerMessageCompleted,
  LoadTextBannerCompleted,
  LoadErrorSSOMessage,
  LoadErrorSSOMessageCompleted,
  LoadErrorSSOMessageFailed,
  UpdateErrorSSOMessageCompleted,
  UpdateErrorSSOMessage,
  LoadUserPropsCompleted,
  UpdateSsoSetting,
  UpdateSsoSettingCompleted,
  CheckSsoKey,
  CheckSsoKeyCompleted,
  UpdateSsoKey,
  UpdateSsoKeyCompleted,
  LoadSSOMetadata,
  LoadSSOMetadataCompleted,
} from './actions/site-settings.action';
import { LoadSiteSupportedLanguageCompleted, UpdateSiteSupportedLanguage, UpdateSiteSupportedLanguageCompleted } from './actions/site-supported-language.action';
import { LoadUserSessionSettings, LoadUserSessionSettingsCompleted, UpdateUserSessionSettings, UpdateUserSessionSettingsCompleted  } from './actions/usersession-settings.action';
import { ErrorSSOSingleInterface, ErrorsSSOInterface } from '../models/error-message';
 import{ LoadErrorSSOSingleMessageCompleted,
  LoadErrorSSOSingleMessage
} from './actions/site-settings.action';

@Injectable()
export class SiteSettingsEffects {
  public source: string = 'SiteSettingsEffects';

  @Effect()
  public loadSiteSettings$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadSiteSettings),
      switchMap(() => {
        return this._siteSettingsConnector.loadAllSiteSettings().pipe(
          map((libraryItems: SiteSettingInterface[]) => new LoadSiteSettingsCompleted(libraryItems)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadUserProps$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadUserProps),
      switchMap(() => {
        return this._siteSettingsConnector.loadUserProps().pipe(
          map((o: SSOStatusInterface) => new LoadUserPropsCompleted(o)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateSiteSetting$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateSiteSetting),
      map((action: UpdateSiteSetting) => action.payload),
      switchMap((settingToUpdate: SiteSettingInterface) => {
        return this._siteSettingsConnector.updateSiteSetting(settingToUpdate).pipe(
          map((updatedSettingData: SiteSettingInterface) => new UpdateSiteSettingCompleted(updatedSettingData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadCustomFields$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadCustomFields),
      switchMap(() => {
        return this._siteSettingsConnector.loadAllCustomFields().pipe(
          map((customFieldItems: CustomFieldInterface[]) => new LoadCustomFieldsCompleted(customFieldItems)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateCustomFields$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateCustomFields),
      map((action: UpdateCustomFields) => action.payload),
      switchMap((customFieldUpdateData: CustomFieldInterface[]) => {
        return this._siteSettingsConnector.updateCustomFields(customFieldUpdateData).pipe(
          map((updatedCustomFieldData: CustomFieldInterface[]) => new UpdateCustomFieldsCompleted(updatedCustomFieldData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadEmailConfigs$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadEmailConfig),
      switchMap(() => {
        return this._siteSettingsConnector.loadAllEmailConfig().pipe(
          map((emailConfigItems: SiteEmailConfigInterface) => new LoadEmailConfigCompleted(emailConfigItems)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateEmailConfigs$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateEmailConfig),
      map((action: UpdateEmailConfig) => action.payload),
      switchMap((EmailConfigUpdateData: SiteEmailConfigInterface) => {
        return this._siteSettingsConnector.updateEmailConfig(EmailConfigUpdateData).pipe(
          map((updatedEmailConfigData: SiteEmailConfigInterface) => new UpdateEmailConfigCompleted(updatedEmailConfigData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadSiteLicense$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadSiteLicense),
      switchMap(() => {
        return this._siteSettingsConnector.loadSiteLicenseData().pipe(
          map((siteLicenseData: LicenseDataInterface) => new LoadSiteLicenseCompleted(siteLicenseData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateSiteLicense$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateSiteLicense),
      map((action: UpdateSiteLicense) => action.payload),
      switchMap((newLicenseKey: string) => {
        return this._siteSettingsConnector.updateSiteLicense(newLicenseKey).pipe(
          map((updateResponseMessage: string) => new UpdateSiteLicenseCompleted(updateResponseMessage)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));
  @Effect()
  public loadSiteSupportedLanguage$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadSiteSupportedLanguage),
      switchMap(() => {
        return this._siteSettingsConnector.loadAllSiteSupportedLanguage().pipe(
          map((languageData: SiteSupportedLanguageInterface[]) => new LoadSiteSupportedLanguageCompleted(languageData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateSiteSupportedLanguage$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateSiteSupportedLanguage),
      map((action: UpdateSiteSupportedLanguage) => action.payload),
      switchMap((languageData: SiteSupportedLanguageInterface[]) => {
        return this._siteSettingsConnector.updateSiteSupportedLanguage(languageData).pipe(
          map((updatedSiteSupportedLanguageData: SiteSupportedLanguageInterface[]) =>
            new UpdateSiteSupportedLanguageCompleted(updatedSiteSupportedLanguageData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  @Effect()
  public loadBackupTranslation$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadBackupTranslate),
      switchMap(() => {
        return this._siteSettingsConnector.loadBackupTranslate().pipe(
          map((backupData: BackupTranslationInterface) => new LoadBackupTranslateCompleted(backupData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateBackupTranslation$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateBackupTranslate),
      map((action: UpdateBackupTranslate) => action.payload),
      switchMap((backupData: BackupTranslationInterface[]) => {
        return this._siteSettingsConnector.updateBackupTranslation(backupData).pipe(
          map((updateBackupTranslationData: BackupTranslationInterface) =>
            new UpdateBackupTranslateCompleted(updateBackupTranslationData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadPasswordSettings$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadPasswordSettings),
      map((action: LoadPasswordSettings) => action.payload),
      switchMap((userId: number) => {
      // switchMap(() => {
        return this._siteSettingsConnector.loadPasswordSettings(userId).pipe(
          map((languageData: PasswordSettingsInterface) => new LoadPasswordSettingsCompleted(languageData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updatePasswordSettings$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdatePasswordSettings),
      map((action: UpdatePasswordSettings) => action.payload),
      switchMap((languageData: PasswordSettingsInterface) => {
        return this._siteSettingsConnector.updatePasswordSettings(languageData).pipe(
          map((UpdatePasswordSettingsData: PasswordSettingsInterface) =>
            new UpdatePasswordSettingsCompleted(UpdatePasswordSettingsData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));
  @Effect()
  public loadUserSessionSettings$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadUserSessionSettings),
      map((action: LoadUserSessionSettings) => action.payload),
      switchMap((userId: number) => {
      // switchMap(() => {
        return this._siteSettingsConnector.loadUserSessionSettings(userId).pipe(
          map((languageData: UserSessionSettingsInterface) => new LoadUserSessionSettingsCompleted(languageData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

@Effect()
public updateBannerCheck$: Observable<Action> = this.actions$
  .pipe(
    ofType(SiteSettingActionType.UpdateBannerCheck),
    map((action: UpdateBannerCheck) => action.payload),
    switchMap((active: boolean) => {
      return this._siteSettingsConnector.updateBannerCheck(active).pipe(
        map((active:ActiveInterface) => new UpdateBannerCheckCompleted(active)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

@Effect()
public checkSsoKey$: Observable<Action> = this.actions$
  .pipe(
    ofType(SiteSettingActionType.CheckSsoKey),
    map((action: CheckSsoKey) => action.payload),
    switchMap((key: string) => {
      return this._siteSettingsConnector.checkSsoKey(key).pipe(
        map((key: string) => new CheckSsoKeyCompleted(key)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

@Effect()
public updateSsoKey$: Observable<Action> = this.actions$
  .pipe(
    ofType(SiteSettingActionType.UpdateSsoKey),
    map((action: UpdateSsoKey) => action.payload),
    switchMap((key: string) => {
      return this._siteSettingsConnector.updateSsoKey(key).pipe(
        map((key: string) => new UpdateSsoKeyCompleted(key)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

@Effect()
public updateSsoSetting$: Observable<Action> = this.actions$
  .pipe(
    ofType(SiteSettingActionType.UpdateSsoSetting),
    map((action: UpdateSsoSetting) => action.payload),
    switchMap((setting: any) => {
      return this._siteSettingsConnector.updateSsoSetting(setting).pipe(
        map((setting: any) => new UpdateSsoSettingCompleted(setting)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

@Effect()
public updateBannerMessage$: Observable<Action> = this.actions$
  .pipe(
    ofType(SiteSettingActionType.UpdateBannerMessage),
    map((action: UpdateBannerMessage) => action.payload),
    switchMap((message: MessageInterface) => {
      return this._siteSettingsConnector.updateBannerMessage(message).pipe(
        map((message: MessageInterface) => new UpdateBannerMessageCompleted(message)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  @Effect()
  public loadMessage$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadMessage),
      switchMap(() =>
          this._siteSettingsConnector.loadBannerMessage()
        .pipe(
          map((message: MessageBannerInterface) =>{
            return new LoadBannerCompleted(message);
          }
            ),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        )
    ));

  @Effect()
  public loadSsoMetadata$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadSSOMetadata),
      switchMap(() =>
          this._siteSettingsConnector.loadSsoMetadata()
        .pipe(
          map((metadata: SSOMetadataInterface) =>{
            return new LoadSSOMetadataCompleted(metadata);
          }
            ),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        )
    ));

  @Effect()
  public loadTextMessage$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadTextMessage),
      switchMap(() =>
          this._siteSettingsConnector.loadBannerTextMessage()
        .pipe(
          map((message: MessageTextInterface) =>{
            return new LoadTextBannerCompleted(message);
          }
            ),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        )
    ));

  @Effect()
  public loadErrorSSOMessage$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadErrorSSOMessage),
      switchMap(() =>
          this._siteSettingsConnector.loadErrorSSOMessage()
        .pipe(
          map((errorSSO: ErrorsSSOInterface[]) =>{
            return new LoadErrorSSOMessageCompleted(errorSSO);
          }
            ),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        )
    ));

    @Effect()
  public loadErrorSSOSingleMessage$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.LoadErrorSSOSingleMessage),
      map((action: LoadErrorSSOSingleMessage) => action.payload),
      switchMap((id: number) =>
          this._siteSettingsConnector.loadErrorSSOSingleMessage(id)
        .pipe(
          map((errorSSO: ErrorSSOSingleInterface) =>{
            return new LoadErrorSSOSingleMessageCompleted(errorSSO);
          }
            ),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        )
    ));

  @Effect()
  public updateErrorSSOMessage$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateErrorSSOMessage),
      map((action: UpdateErrorSSOMessage) => action.payload),
      switchMap((error: ErrorSSOSingleInterface) =>
          this._siteSettingsConnector.updateErrorSSOMessage(error)
        .pipe(
          map((errorSSO: ErrorSSOSingleInterface) =>{
            return new UpdateErrorSSOMessageCompleted(errorSSO);
          }
            ),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        )
    ));

  @Effect()
  public updateUserSessionSettings$: Observable<Action> = this.actions$
    .pipe(
      ofType(SiteSettingActionType.UpdateUserSessionSettings),
      map((action: UpdateUserSessionSettings) => action.payload),
      switchMap((languageData: UserSessionSettingsInterface) => {
        return this._siteSettingsConnector.updateUserSessionSettings(languageData).pipe(
          map((UpdateUserSessionSettingsData: UserSessionSettingsInterface) =>
            new UpdateUserSessionSettingsCompleted(UpdateUserSessionSettingsData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  constructor(private readonly actions$: Actions, private readonly _siteSettingsConnector: SiteSettingsRestConnectorService) {}
}
